import { CallStateEnum } from '@models/call-state.model.ts';
import {
  MEDIA_LIBRARY_INITIAL_STATE,
  MediaLibraryActionParams
} from './media-library.model.ts';
import {
  getMediaLibraryFolders,
  createFolder,
  renameFolder,
  deleteFolder
} from '@api/media-library-api.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import axios from 'axios';

const { showSnackbar } = useSnackbarStore.getState();

export const foldersSlice = (set: any, get: any) => ({
  ...MEDIA_LIBRARY_INITIAL_STATE,

  getMediaLibraryFolders: async (id?: string, title?: string) => {
    set({ callState: CallStateEnum.Loading });

    const { sort, search, breadcrumb, previousBreadcrumb, currentFolder } =
      get();
    const hasSearchValue = Object.values(search || {}).some(
      (value) => value !== '' && value !== null && value !== undefined
    );

    const folderId = hasSearchValue ? null : id;

    try {
      const params: any = { query: { limit: 5000 } };

      if (hasSearchValue) {
        params.query.search = {};
        Object.entries(search).forEach(([key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            params.query.search[key] = value;
          }
        });
      }

      if (sort && sort[0]) params.query.sort = `${sort[0]},${sort[1]}`;

      const apiData = await getMediaLibraryFolders(folderId, params);

      const data = apiData.rows.map((folder: any) => ({
        id: folder._id,
        parentId: folder.parentId,
        createdAt: folder.createdAt,
        updatedAt: folder.updatedAt,
        orgId: folder.orgId,
        title: folder.name,
        folderCount: folder.folderCount ?? 0,
        assetsCount: folder.mediaCount ?? 0
      }));

      const checkedItems: Record<string, boolean> = {};
      data.forEach((folder: any) => (checkedItems[folder.id] = false));

      if (hasSearchValue) {
        if (!previousBreadcrumb) {
          set({ previousBreadcrumb: breadcrumb });
        }

        set({
          breadcrumb: [{ id: 'root', title: '/' }],
          callState: CallStateEnum.Loaded,
          foldersData: data,
          checkedItems
        });
        return;
      }

      const baseBreadcrumb = previousBreadcrumb || breadcrumb;

      set({ previousBreadcrumb: null });

      set((state: any) => {
        const breadcrumbPath = [...baseBreadcrumb];

        const existingIndex = breadcrumbPath.findIndex((b) => b.id === id);
        const newBreadcrumb =
          existingIndex !== -1
            ? breadcrumbPath.slice(0, existingIndex + 1)
            : [...breadcrumbPath, { id, title: title || 'Unknown' }];

        return {
          breadcrumb: newBreadcrumb,
          callState: CallStateEnum.Loaded,
          foldersData: data,
          checkedItems,
          currentFolder: { id, title: title || 'Unknown' }
        };
      });
    } catch (error: any) {
      set({ callState: CallStateEnum.Error });
      if (axios.isAxiosError(error))
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
    }
  },

  createFolder: async ({ data, handleClose }: MediaLibraryActionParams) => {
    set({ folderCallState: CallStateEnum.Loading });
    try {
      await createFolder(data);
      set({ folderCallState: CallStateEnum.Loaded });
      handleClose?.();
      get().getMediaLibraryFolders(
        get().currentFolder.id,
        get().currentFolder.title
      );
      showSnackbar('snackbar_success_folder_text', SnackbarType.Success);
    } catch (error: any) {
      set({ folderCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error))
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
    }
  },

  renameFolder: async ({ data, id, handleClose }: MediaLibraryActionParams) => {
    set({ folderCallState: CallStateEnum.Loading });
    try {
      await renameFolder(data, id as any);
      set({ folderCallState: CallStateEnum.Loaded });
      handleClose?.();
      get().getMediaLibraryFolders(
        get().currentFolder.id,
        get().currentFolder.title
      );
      showSnackbar('snackbar_update_folder_text', SnackbarType.Success);
    } catch (error: any) {
      set({ folderCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error))
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
    }
  },

  deleteFolder: async ({ ids, handleClose }: MediaLibraryActionParams) => {
    set({ folderCallState: CallStateEnum.Loading });
    try {
      await deleteFolder(ids as any);
      set({ folderCallState: CallStateEnum.Loaded });
      handleClose?.();
      get().getMediaLibraryFolders(
        get().currentFolder.id,
        get().currentFolder.title
      );
      showSnackbar('snackbar_delete_folder_text', SnackbarType.Success);
    } catch (error: any) {
      set({ folderCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error))
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
    }
  },

  setCurrentFolder: (folder: any) =>
    set((state: any) => ({
      currentFolder: folder,
      pagination: { ...state.pagination, page: 1 }
    })),

  setBreadcrumb: (path: any) => set({ breadcrumb: path }),

  goToBreadcrumb: (id: string) => {
    const folder =
      id === 'root'
        ? { id: 'root', title: '/' }
        : get().breadcrumb.find((b: any) => b.id === id);
    if (!folder) return;

    set((state: any) => {
      const index = state.breadcrumb.findIndex((b: any) => b.id === id);
      const newBreadcrumb =
        index !== -1 ? state.breadcrumb.slice(0, index + 1) : state.breadcrumb;
      return { breadcrumb: newBreadcrumb, currentFolder: folder };
    });
  },

  toggleItemChecked: (id: string, checked: boolean) =>
    set((state: any) => ({
      checkedItems: { ...state.checkedItems, [id]: checked }
    })),

  toggleSelectAll: (checked: boolean) => {
    const allChecked: Record<string, boolean> = {};
    get().foldersData.forEach(
      (folder: any) => (allChecked[folder.id] = checked)
    );
    set({ checkedItems: allChecked });
  },

  setEditingFolder: (folder: any) => set({ editingFolder: folder }),
  resetEditingFolder: () => set({ editingFolder: null }),
  resetToggleItemChecked: () => set({ checkedItems: {} }),
  resetMediaLibrary: () =>
    set((state: any) => ({
      ...MEDIA_LIBRARY_INITIAL_STATE,
      uploadingFiles: state.uploadingFiles,
      selectedMedia: state.selectedMedia
    }))
});
